import { useCallback, useEffect, useMemo, useState } from 'react';
import apiService from '../services/api';
import TeamUniverseNav from '../components/equipe/TeamUniverseNav';
import TeamWeekPlanning from '../components/equipe/TeamWeekPlanning';
import PresencesKpiRow from '../components/equipe/presences/PresencesKpiRow';
import PresencesMemberList from '../components/equipe/presences/PresencesMemberList';
import PresencesMemberDetail from '../components/equipe/presences/PresencesMemberDetail';
import PresencesDateExceptionDrawer from '../components/equipe/presences/PresencesDateExceptionDrawer';
import { computePresencesKpis } from '../utils/equipePresencesToday';
import { getParisWeekApiRange } from '../utils/equipeWeekParis';
import { mapInstructeurHoraireExceptionsResponse } from '../utils/equipeDateHoraireExceptions';
import { getOperationalCommercantId } from '../utils/operationalCommercantScope';

export default function EquipePresences() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekLoading, setWeekLoading] = useState(false);
  const [horairesByInstructeur, setHorairesByInstructeur] = useState({});
  const [absencesByInstructeur, setAbsencesByInstructeur] = useState({});
  const [dateExceptionsByInstructeur, setDateExceptionsByInstructeur] = useState({});
  const [prestationsCatalog, setPrestationsCatalog] = useState([]);
  const [dateException, setDateException] = useState(null);

  const commercantId = getOperationalCommercantId();

  const prestataires = useMemo(
    () => (members || []).filter((m) => m.isIntervenant && m.instructeurId),
    [members],
  );

  const fetchEquipe = useCallback(async () => {
    if (!commercantId) {
      setMembers([]);
      setLoading(false);
      setError('Aucune boutique associée à ce compte.');
      return [];
    }
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getEquipe(commercantId);
      const list = data.members || [];
      setMembers(list);
      return list;
    } catch (err) {
      setError(err.data?.error?.message || err.message || 'Erreur lors du chargement');
      return [];
    } finally {
      setLoading(false);
    }
  }, [commercantId]);

  const loadWeekData = useCallback(async () => {
    if (!commercantId) return;
    const ids = prestataires.map((m) => m.instructeurId).filter(Boolean);
    if (!ids.length) {
      setHorairesByInstructeur({});
      setAbsencesByInstructeur({});
      setDateExceptionsByInstructeur({});
      return;
    }

    setWeekLoading(true);
    try {
      const { days } = getParisWeekApiRange(weekOffset);
      const from = days[0].ymd;
      const to = days[6].ymd;
      const perInstructeur = await Promise.all(
        ids.flatMap((id) => [
          apiService.getInstructeurHorairesConfig(id),
          apiService.getInstructeurIndisponibilites(id, { actif: true }),
          apiService.getInstructeurHoraireDateExceptions(id, { from, to }),
        ]),
      );

      const horairesMap = {};
      const absencesMap = {};
      const exceptionsMap = {};
      for (let i = 0; i < ids.length; i++) {
        const instructeurId = ids[i];
        horairesMap[instructeurId] = perInstructeur[i * 3];
        absencesMap[instructeurId] = perInstructeur[i * 3 + 1]?.indisponibilites || [];
        exceptionsMap[instructeurId] = mapInstructeurHoraireExceptionsResponse(perInstructeur[i * 3 + 2]);
      }
      setHorairesByInstructeur(horairesMap);
      setAbsencesByInstructeur(absencesMap);
      setDateExceptionsByInstructeur(exceptionsMap);
    } catch (err) {
      setError(err.message || 'Erreur chargement présences semaine');
    } finally {
      setWeekLoading(false);
    }
  }, [commercantId, prestataires, weekOffset]);

  const loadMemberDetail = useCallback(
    async (memberId) => {
      if (!memberId || !commercantId) {
        setMemberDetail(null);
        return;
      }
      setDetailLoading(true);
      try {
        const data = await apiService.getEquipeMember(memberId, commercantId);
        setMemberDetail(data.member);
      } catch {
        const fallback = members.find((m) => m.id === memberId) || null;
        setMemberDetail(fallback);
      } finally {
        setDetailLoading(false);
      }
    },
    [commercantId, members],
  );

  useEffect(() => {
    fetchEquipe();
  }, [fetchEquipe]);

  useEffect(() => {
    if (members.length > 0 && !selectedId) {
      setSelectedId(members[0].id);
    }
  }, [members, selectedId]);

  useEffect(() => {
    loadWeekData();
  }, [loadWeekData]);

  useEffect(() => {
    if (commercantId) {
      apiService
        .getPrestationsPersonnelForInstructeurs(commercantId)
        .then((d) => setPrestationsCatalog(d.prestations || []))
        .catch(() => setPrestationsCatalog([]));
    }
  }, [commercantId]);

  useEffect(() => {
    if (selectedId) {
      loadMemberDetail(selectedId);
    } else {
      setMemberDetail(null);
    }
  }, [selectedId, loadMemberDetail]);

  const kpis = useMemo(
    () =>
      computePresencesKpis(
        members,
        horairesByInstructeur,
        absencesByInstructeur,
        dateExceptionsByInstructeur,
      ),
    [members, horairesByInstructeur, absencesByInstructeur, dateExceptionsByInstructeur],
  );

  const selectedMember = memberDetail || members.find((m) => m.id === selectedId) || null;
  const instructeurId = selectedMember?.instructeurId;
  const memberHoraires = instructeurId ? horairesByInstructeur[instructeurId] : null;
  const memberAbsences = instructeurId ? absencesByInstructeur[instructeurId] || [] : [];
  const memberDateExceptions = instructeurId
    ? dateExceptionsByInstructeur[instructeurId] || null
    : null;

  const handleSelectMember = (id) => {
    setSelectedId(id);
  };

  const handleCellClick = ({ member, day }) => {
    if (!member?.id || !day) return;
    setSelectedId(member.id);
    setDateException({ member, day });
  };

  const handleExceptionSaved = async () => {
    await loadWeekData();
    if (selectedId) {
      await loadMemberDetail(selectedId);
    }
  };

  const exceptionMember = dateException?.member || selectedMember;
  const exceptionInstructeurId = exceptionMember?.instructeurId;
  const exceptionHoraires = exceptionInstructeurId
    ? horairesByInstructeur[exceptionInstructeurId]
    : null;
  const exceptionAbsences = exceptionInstructeurId
    ? absencesByInstructeur[exceptionInstructeurId] || []
    : [];
  const exceptionDateOverrides = exceptionInstructeurId
    ? dateExceptionsByInstructeur[exceptionInstructeurId] || {}
    : {};

  if (loading && !members.length) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-violet-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6">
      <TeamUniverseNav subtitle="Présences et disponibilités de la semaine" />

      {error ? (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      ) : null}

      <PresencesKpiRow kpis={kpis} />

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(240px,0.75fr)_minmax(0,1.35fr)_minmax(280px,0.85fr)]">
        <div className="min-h-[320px] xl:min-h-[520px]">
          <PresencesMemberList
            members={members}
            selectedId={selectedId}
            onSelect={handleSelectMember}
            horairesByInstructeur={horairesByInstructeur}
            absencesByInstructeur={absencesByInstructeur}
            dateExceptionsByInstructeur={dateExceptionsByInstructeur}
          />
        </div>

        <div className="min-h-[400px]">
          <TeamWeekPlanning
            members={members}
            selectedId={selectedId}
            onSelectMember={(id) => handleSelectMember(id)}
            weekOffset={weekOffset}
            onWeekOffsetChange={setWeekOffset}
            horairesByInstructeur={horairesByInstructeur}
            absencesByInstructeur={absencesByInstructeur}
            dateExceptionsByInstructeur={dateExceptionsByInstructeur}
            rdvList={[]}
            loading={weekLoading}
            onCellClick={handleCellClick}
          />
        </div>

        <div className="hidden min-h-[320px] xl:block xl:min-h-[520px]">
          <PresencesMemberDetail
            member={selectedMember}
            prestationsCatalog={prestationsCatalog}
            horairesConfig={memberHoraires}
            absences={memberAbsences}
            dateExceptionsByDate={memberDateExceptions}
            weekOffset={weekOffset}
            loading={detailLoading && !selectedMember}
          />
        </div>
      </div>

      <div className="xl:hidden">
        <PresencesMemberDetail
          member={selectedMember}
          prestationsCatalog={prestationsCatalog}
          horairesConfig={memberHoraires}
          absences={memberAbsences}
          dateExceptionsByDate={memberDateExceptions}
          weekOffset={weekOffset}
          loading={detailLoading && !selectedMember}
        />
      </div>

      <PresencesDateExceptionDrawer
        open={Boolean(dateException)}
        onClose={() => setDateException(null)}
        member={exceptionMember}
        day={dateException?.day}
        horairesConfig={exceptionHoraires}
        absences={exceptionAbsences}
        dateExceptionsByDate={exceptionDateOverrides}
        onSaved={handleExceptionSaved}
      />
    </div>
  );
}
